import { HTMLProps, ReactNode, useContext } from 'react';
import { U21HelpTooltipProps } from 'app/shared/u21-ui/components/display/U21HelpTooltip';

import { getDOMProps } from 'app/shared/utils/react';
import { isEqual } from 'lodash';
import styled from 'styled-components';

import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FieldSubscription } from 'final-form';
import { FormControl } from '@mui/material';
import { U21FormContext } from 'app/shared/u21-ui/components/form/U21FormContext';
import { U21FormLabel } from 'app/shared/u21-ui/components/form/U21FormLabel';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';

export type U21FieldArrayRenderProps<FieldValue> = FieldArrayRenderProps<
  FieldValue,
  HTMLElement
>;
export interface U21FormFieldArrayProps<FieldValue> extends Omit<
  HTMLProps<HTMLDivElement>,
  'children' | 'type'
> {
  children: (props: U21FieldArrayRenderProps<FieldValue>) => ReactNode;
  description?: string;
  disabled?: boolean;
  help?: U21HelpTooltipProps['help'];
  hidden?: boolean;
  label?: string;
  name: string;
  required?: boolean;
  subscription?: FieldSubscription;
}

export const U21FormFieldArray = <FieldValue,>({
  children,
  description,
  disabled: disabledProp,
  help,
  hidden,
  label,
  name,
  required,
  subscription,
  ...rest
}: U21FormFieldArrayProps<FieldValue>) => {
  const { disabled: disabledContext, loading } = useContext(U21FormContext);

  if (hidden) {
    return null;
  }

  return (
    <FieldArray<FieldValue>
      name={name}
      isEqual={isEqual}
      subscription={subscription}
    >
      {(fieldArrayProps: FieldArrayRenderProps<FieldValue, HTMLElement>) => {
        const disabled = disabledContext || disabledProp || loading;
        return (
          <Container {...getDOMProps(rest)}>
            <FormControl fullWidth>
              {Boolean(label) && (
                <StyledU21FormLabel
                  $hasDescription={Boolean(description)}
                  disabled={disabled}
                  help={help}
                  required={required}
                >
                  {label}
                </StyledU21FormLabel>
              )}
              {description && (
                <StyledU21Typography
                  aria-hidden
                  color={disabled ? 'text.disabled' : 'text.secondary'}
                  variant="body2"
                >
                  {description}
                </StyledU21Typography>
              )}
              {typeof children === 'function'
                ? children(fieldArrayProps)
                : children}
            </FormControl>
          </Container>
        );
      }}
    </FieldArray>
  );
};

const Container = styled.div`
  min-width: 180px;
`;

const StyledU21FormLabel = styled(U21FormLabel)<{ $hasDescription?: boolean }>`
  margin-bottom: ${(props) => (props.$hasDescription ? 0 : 8)}px;
`;

const StyledU21Typography = styled(U21Typography)`
  margin-bottom: 8px;
`;
